import * as React from 'react'
import { useAppForm } from '@/hooks/app-form'
import { useApiMutation, useApiQuery } from '@/api/ApiQueryServiceProvider'
import {
  ProvisionDeviceFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type ProvisionDeviceFormData,
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import {
  renderSelectControl,
  renderButtonControl,
  type ControlConfig
} from '../../formRenderFunctions'
import { useSelectOptionsMap } from '@/hooks/useSelectOptions'
import { coerceFieldValue } from '@/lib/utils'
import { AppLogger } from '@/lib/logger'
import { useSecurityContext } from '@/contexts/SecurityContext'

const log = AppLogger.getLogger(import.meta)

export { ProvisionDeviceFormSchema, type ProvisionDeviceFormData } from './schema'

export type ProvisionDeviceComposerProps = {
  deviceNumber: number
  deviceProcurementInfoNumber?: number
  deviceDeliveryDate?: number
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  showHeader?: boolean
  submitLabel?: string
}

const validateField = (name: keyof ProvisionDeviceFormData, value: unknown): string | null => {
  const shape = ProvisionDeviceFormSchema.shape[name]
  if (!shape) return null

  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

export function ProvisionDeviceComposer(props: ProvisionDeviceComposerProps) {
  const { deviceNumber, deviceProcurementInfoNumber, deviceDeliveryDate, onSuccess, onError, onCancel, showHeader = true, submitLabel } = props
  const { userInfo } = useSecurityContext()

  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  // Fetch network corridor options
  const { optionsMap: corridorOptionsMap, loadingMap: corridorLoadingMap } = useSelectOptionsMap({
    networkCorridor: {
      endpoint: 'getNetworkCorridors',
      dataKey: 'networkCorridors',
      valueField: 'networkCorridorNumber',
      labelField: 'networkCorridorName',
    },
  })

  // Get selected corridor value from form state (we'll need to watch it)
  const [selectedCorridor, setSelectedCorridor] = React.useState<string>('')

  // Fetch network site options (load all sites once a corridor is selected)
  const { optionsMap: siteOptionsMap, loadingMap: siteLoadingMap } = useSelectOptionsMap({
    networkSite: {
      endpoint: 'getNetworkSites',
      dataKey: 'networkSites',
      valueField: 'networkSiteNumber',
      labelField: 'networkSiteName',
      queryOptions: {
        enabled: !!selectedCorridor, // Only fetch when a corridor is selected
      },
    },
  })

  // Fetch existing procurementInfo for merging (must be at top level)
  const { data: deviceDataRaw, isLoading: isDeviceLoading } = useApiQuery('getDeviceByNumber', { deviceNumber });
  const deviceData = deviceDataRaw?.content;

  const provisionProcurement = useApiMutation('upsertDeviceProcurementInfo', 'POST', {
    onSuccess: () => {},
    onError: (error) => {
      onError?.(error)
    },
  })

  const provisionProvisioning = useApiMutation('upsertDeviceProvisioningInfo', 'POST', {
    onSuccess: () => {},
    onError: (error) => {
      onError?.(error)
    },
  })

  const upsertDevice = useApiMutation('upsertDevice', 'POST', {
    invalidates: ['getDevices', 'getDeviceByNumber'],
    onSuccess: () => {},
    onError: (error) => {
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: ProvisionDeviceFormSchema,
    defaultValues: {
      networkCorridor: '',
      networkSite: '',
    } as ProvisionDeviceFormData,
    onSubmit: async ({ value }) => {
      const formValue = value as ProvisionDeviceFormData
      // Wait for device data to load
      if (isDeviceLoading) return;

      // Build procurement/provisioning info payloads
      const { provisioningInfo } = transformToApiRequest(formValue, deviceNumber, deviceProcurementInfoNumber, deviceDeliveryDate);

      // Merge with existing procurementInfo if available - preserve all existing fields
      const existingProcurementInfo = deviceData?.procurementInfo || {};

      // Build merged procurement info, only including new fields if they have values
      const mergedProcurementInfo: any = {
        ...existingProcurementInfo,
        deviceNumber,
        deviceProcurementInfoNumber,
        deviceProcurementStatus: 'testing',
      };

      // Only update deviceDeliveryDate if it was provided and has a value
      if (deviceDeliveryDate) {
        mergedProcurementInfo.deviceDeliveryDate = deviceDeliveryDate;
      }

      log.debug('[provisionDevice] Merged procurement info:', mergedProcurementInfo);

      // Merge with existing provisioningInfo if available - preserve all existing fields
      const existingProvisioningInfo: any = deviceData?.provisioningInfo || {};
      const existingProvisioningInfoNumber = existingProvisioningInfo?.deviceProvisioningInfoNumber;

      // Build merged provisioning info - preserve existing fields, update with new values
      let provisioningInfoWithId: any = {
        ...existingProvisioningInfo,  // Preserve all existing fields
        ...provisioningInfo,           // Overwrite with new values from form
      };

      const currentUserNumber =
        userInfo?.userNumber && userInfo.userNumber >= 1000000000 ? userInfo.userNumber : undefined
      const existingRequestedBy =
        existingProvisioningInfo?.deviceProvisionedRequestedBy &&
        existingProvisioningInfo.deviceProvisionedRequestedBy >= 1000000000
          ? existingProvisioningInfo.deviceProvisionedRequestedBy
          : undefined
      const existingProvisionedDeviceNumber =
        existingProvisioningInfo?.provisionedDeviceNumber &&
        existingProvisioningInfo.provisionedDeviceNumber >= 1000000000
          ? existingProvisioningInfo.provisionedDeviceNumber
          : undefined

      // Always keep required IDs valid to avoid backend defaulting to 0.
      provisioningInfoWithId.provisionedDeviceNumber = existingProvisionedDeviceNumber || deviceNumber
      provisioningInfoWithId.deviceProvisionedRequestedBy = currentUserNumber || existingRequestedBy

      // Always include the provisioning info number if it exists (for updates)
      if (existingProvisioningInfoNumber) {
        provisioningInfoWithId.deviceProvisioningInfoNumber = existingProvisioningInfoNumber;
      }

      log.debug('[provisionDevice] Starting provision workflow:', {
        deviceNumber,
        existingProvisioningInfoNumber,
        existingProvisioningInfo,
        provisioningInfoWithId,
        mergedProcurementInfo,
      });

      // Update procurementInfo and provisioningInfo
      await provisionProcurement.mutateAsync({ deviceNumber, procurementInfo: mergedProcurementInfo });
      const provisioningResponse = await provisionProvisioning.mutateAsync({ deviceNumber, provisioningInfo: provisioningInfoWithId });

      log.debug('[provisionDevice] Provisioning response:', provisioningResponse);

      // Extract the deviceProvisioningInfoNumber from the response
      const createdProvisioningInfoNumber = provisioningResponse?.content?.provisioningInfo?.deviceProvisioningInfoNumber;

      log.debug('[provisionDevice] Extracted provisioningInfoNumber:', { createdProvisioningInfoNumber, existingProvisioningInfoNumber });

      // Update deviceStatus to 'testing' AND link to provisioning info
      const deviceUpdate = {
        device: {
          ...deviceData?.device,
          deviceNumber,
          deviceStatus: 'testing',
          deviceProvisioningInfoNumber: createdProvisioningInfoNumber || existingProvisioningInfoNumber,
        },
      };

      log.debug('[provisionDevice] Updating device with:', deviceUpdate);

      await upsertDevice.mutateAsync(deviceUpdate);

      // Success callback
      onSuccess?.();
    },
  })

  // Update selectedCorridor when it changes via onChange handler
  React.useEffect(() => {
    if (selectedCorridor) {
      // Clear networkSite when corridor changes
      form.setFieldValue('networkSite', '')
    }
  }, [selectedCorridor, form])

  const renderControl = (control: any) => {
    const isRequired = control.required

    if (control.htmlControl === 'select') {
      // Get options from API or YAML
      let options: any[] = []
      let isLoading = false

      if (control.name === 'networkCorridor') {
        options = corridorOptionsMap.networkCorridor || []
        isLoading = corridorLoadingMap.networkCorridor || false
      } else if (control.name === 'networkSite') {
        options = siteOptionsMap.networkSite || []
        isLoading = siteLoadingMap.networkSite || false
      } else {
        options = FormConfigLoader.normalizeSelectOptions(control.selectOptions || [])
        isLoading = false
      }

      return (
        <form.AppField 
          key={control.name}
          name={control.name}
          validators={{
            onChange: ({ value }) => {
              // Update selectedCorridor when networkCorridor changes
              if (control.name === 'networkCorridor') {
                setSelectedCorridor(String(value || ''))
              }
              return undefined
            },
            onBlur: ({ value }) => 
              validateField(control.name as keyof ProvisionDeviceFormData, coerceFieldValue(control.name, value)) || undefined,
          }}
        >
          {(bound) => {
            const config: ControlConfig = {
              name: control.name,
              label: control.label,
              placeholder: control.placeholder,
              required: isRequired,
              visibility: control.visibility ?? true,
              disabled: (control.disabled ?? false) || (control.name === 'networkSite' && !selectedCorridor),
              options: options,
              isLoading: isLoading,
            }
            return renderSelectControl(config, bound)
          }}
        </form.AppField>
      )
    }

    return null
  }

  const isSubmitting = provisionProcurement.isPending || provisionProvisioning.isPending || upsertDevice.isPending

  return (
    <form.AppForm>
      <div className="space-y-6">
        {showHeader && (
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">{FORM_CONFIG.formTitle}</h2>
            <p className="text-sm text-muted-foreground">
              {FORM_CONFIG.formDescription}
            </p>
          </div>
        )}

        {groups.map((group) => {
          const groupControls = controlsByGroup.get(group.formGroupName) || []
          if (groupControls.length === 0) return null
          return (
            <div key={group.formGroupName} className="space-y-4">
              {groupControls.map(renderControl)}
            </div>
          )
        })}

        <div className="pt-4 flex justify-center gap-3">
          {renderButtonControl({
            label: FORM_CONFIG.cancelButton?.label ?? '',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button'
          })}
          
          {renderButtonControl({
            label: isSubmitting 
              ? (FORM_CONFIG.submitButton?.loadingLabel ?? 'Provisioning...') 
              : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? 'Provision'),
            variant: 'default',
            type: 'submit'
          }, { SubmitButton: form.SubmitButton })}
        </div>
      </div>
    </form.AppForm>
  )
}
